import { useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import ResizableImage from './ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { reuploadExternalImages } from '../utils/pasteImages';
import api from '../utils/api';

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

const cursorBuilder = (user) => {
  const cursor = document.createElement('span');
  cursor.className = 'yjs-cursor';
  cursor.style.borderColor = user.color;
  const label = document.createElement('span');
  label.className = 'yjs-cursor-label';
  label.style.backgroundColor = user.color;
  label.textContent = user.name;
  cursor.appendChild(label);
  return cursor;
};

const SectionEditor = forwardRef(function SectionEditor(
  { ydoc, provider, sectionId, onUpdate, onFocus, logId, onAttachRefresh },
  ref
) {
  const editorRef = useRef(null);

  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment(`sec-${sectionId}`);
      return [
        ySyncPlugin(fragment),
        yCursorPlugin(provider.awareness, { cursorBuilder }),
        yUndoPlugin(),
      ];
    },
  }), [ydoc, provider, sectionId]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow, TableCell, TableHeader,
    ],
    autofocus: false,
    onUpdate: ({ editor }) => onUpdate?.(editor.getHTML()),
    onFocus: ({ editor }) => onFocus?.(editor),
    editorProps: {
      attributes: { class: 'prose prose-invert max-w-none focus:outline-none min-h-[80px] px-4 py-3' },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        const nonImageFiles = Array.from(files).filter(f => !f.type.startsWith('image/'));
        if (nonImageFiles.length > 0) {
          event.preventDefault();
          if (logId) {
            (async () => {
              for (const f of nonImageFiles) {
                const fd = new FormData();
                fd.append('file', f);
                fd.append('entityType', 'research');
                fd.append('entityId', String(logId));
                await api.post('/files', fd).catch(() => {});
              }
              onAttachRefresh?.();
            })();
          }
          if (imageFiles.length === 0) return true;
        }
        event.preventDefault();
        imageFiles.forEach(async (f) => {
          const url = await uploadImage(f);
          view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/')) {
              event.preventDefault();
              const file = item.getAsFile();
              if (!file) continue;
              (async () => {
                const url = await uploadImage(file);
                view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
              })();
              return true;
            }
          }
        }
        const html = event.clipboardData?.getData('text/html');
        if (html && /<img[^>]+src=["']https?:\/\//i.test(html)) {
          event.preventDefault();
          (async () => {
            const processed = await reuploadExternalImages(html);
            editorRef.current?.commands.insertContent(processed);
          })();
          return true;
        }
        return false;
      },
    },
  }, [YjsExtension]);

  useEffect(() => { editorRef.current = editor; }, [editor]);

  useImperativeHandle(ref, () => ({ getHTML: () => editor?.getHTML() || '' }), [editor]);

  return <EditorContent editor={editor} />;
});

export default SectionEditor;
